import {
  QUIKCOINS_BALANCE_API,
  REWARDS_DAILY_CHECKIN_API,
  QUIKLEARN_BACKEND_URL,
  REWARDS_REDEEM_API,
  API_KEY,
} from 'config/app.config';

const buildHeaders = (token, includeAuth = true) => {
  const headers = { 'Content-Type': 'application/json' };
  if (API_KEY && String(API_KEY).toLowerCase() !== 'dummy') {
    headers['x-api-key'] = API_KEY;
  }
  if (includeAuth && token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const normalizeBaseUrl = (url) => String(url || '').replace(/\/$/, '');

const getBaseUrls = () => {
  const candidates = [QUIKLEARN_BACKEND_URL].filter(Boolean);
  const normalized = candidates.map(normalizeBaseUrl).filter(Boolean);
  return [...new Set(normalized)];
};

const safeParseJson = async (response) => {
  try {
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
};

const fetchJson = async (baseUrl, endpoint, options) => {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${baseUrl}${normalizedEndpoint}`;
  const response = await fetch(url, options);
  const data = await safeParseJson(response);
  return { response, data };
};

export const getQuikcoinsBalance = async ({ userId, token }) => {
  if (!userId) throw new Error('getQuikcoinsBalance: userId is required');
  const endpoint =
    typeof QUIKCOINS_BALANCE_API === 'function'
      ? QUIKCOINS_BALANCE_API(userId)
      : `/api/quikcoins/balance/${userId}`;
  const bases = getBaseUrls();
  let lastError;

  for (const baseUrl of bases) {
    try {
      // The balance route needs a caller: the launch token is verified there (owner items 12/13,
      // Sol owner14 Q1). Retrying without it can only fail.
      const { response, data } = await fetchJson(baseUrl, endpoint, {
        method: 'GET',
        headers: buildHeaders(token, true),
      });

      if (response.ok) {
        return data;
      }

      lastError = data?.message || data?.error || response.statusText;
    } catch (error) {
      lastError = error?.message || error;
    }
  }

  throw new Error(lastError || 'Failed to fetch quikcoins balance');
};

/**
 * Claim the daily check-in reward.
 *
 * The server pays daily login at most once per reward day and takes the learner
 * from the token, so this is safe to call on every open and needs no body. It
 * never throws: a check-in that cannot be paid must not disturb the session.
 */
export const claimDailyCheckin = async ({ token }) => {
  const endpoint = REWARDS_DAILY_CHECKIN_API || '/api/rewards/daily-checkin';

  for (const baseUrl of getBaseUrls()) {
    try {
      const { response, data } = await fetchJson(baseUrl, endpoint, {
        method: 'POST',
        headers: buildHeaders(token, true),
        body: JSON.stringify({}),
      });
      if (response.ok) {
        return data?.data || null;
      }
    } catch (_) {
      // Try the next base url, then give up quietly.
    }
  }
  return null;
};

export const requestReward = async ({ reward, userId, token }) => {
  if (!reward) throw new Error('requestReward: reward is required');
  if (!userId) throw new Error('requestReward: userId is required');
  const redeemEndpoint = REWARDS_REDEEM_API || '/api/quikcoins/redeem-reward';
  const bases = getBaseUrls();
  let lastError;

  for (const baseUrl of bases) {
    try {
      let { response, data } = await fetchJson(baseUrl, redeemEndpoint, {
        method: 'POST',
        headers: buildHeaders(token, true),
        body: JSON.stringify({
          userId,
          name: reward.name,
          cost: reward.cost,
          rewardId: reward.id,
        }),
      });

      if (response.status === 401 && token) {
        ({ response, data } = await fetchJson(baseUrl, redeemEndpoint, {
          method: 'POST',
          headers: buildHeaders(token, false),
          body: JSON.stringify({
            userId,
            name: reward.name,
            cost: reward.cost,
            rewardId: reward.id,
          }),
        }));
      }

      if (response.ok) {
        return { success: true, data };
      }

      lastError = data?.message || data?.error || response.statusText;
    } catch (error) {
      lastError = error?.message || error;
    }
  }

  return { success: false, error: lastError || 'Reward redemption failed' };
};
